import React, { useEffect, useMemo, useState } from 'react';
import {
    Container, Grid, Button, TextField, Select, MenuItem,
    FormControl, InputLabel, Typography, Divider, Checkbox, ListItemText,
    Snackbar, Alert, CircularProgress, Box
} from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import Layout from '../Layout';
import { DEPARTMENT_OPTIONS } from '../constants/departments';

const theme = createTheme({
    palette: { primary: { main: '#1976d2' }, secondary: { main: '#d32f2f' } },
    shape: { borderRadius: 12 },
    typography: { h6: { fontWeight: 700 }, button: { textTransform: 'none', fontWeight: 600 } },
});

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.08)',
    backgroundColor: '#fff',
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
    margin: theme.spacing(3, 0),
    backgroundColor: theme.palette.primary.main,
}));

export default function EditUser() {
    const params = useParams();
    const userId = params.userId || params.id; // ✅ handles either route param name
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // dropdown options (like Users.jsx)
    const [companies, setCompanies] = useState([]);
    const [locations, setLocations] = useState([]);
    const [lawAreas, setLawAreas] = useState([]);

    // form (match your schema fields)
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        role: '',
        company: '',
        location: [],
        lawarea: [], // ✅ schema is lawarea (lowercase)
        departments: [],
    });

    const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', msg: '' });

    const firstName = useMemo(() => String(form.name || '').trim().split(' ')[0] || '', [form.name]);
    const lastName = useMemo(() => String(form.name || '').trim().split(' ').slice(1).join(' ') || '', [form.name]);

    useEffect(() => {
        const load = async () => {
            try {
                if (!userId) {
                    setSnackbar({ open: true, severity: 'error', msg: 'Missing userId in URL.' });
                    setLoading(false);
                    return;
                }

                setLoading(true);

                // 1) meta for dropdowns (same pattern as Users.jsx)
                const [{ data: meta }, lawRes] = await Promise.all([
                    api.get('detail/userinfo?view=meta'),
                    api.get('tracker/law-area'),
                ]);

                const companyNames = Array.isArray(meta?.companyDetails?.company_name)
                    ? meta.companyDetails.company_name
                    : meta?.companyDetails?.company_name
                        ? [meta.companyDetails.company_name]
                        : [];
                setCompanies(companyNames);

                const rawLoc =
                    meta?.companyDetails?.locations ??
                    meta?.companyDetails?.location ??
                    [];
                const asArray = Array.isArray(rawLoc) ? rawLoc : Object.values(rawLoc || {});
                const flat = [...new Set(asArray.flat().filter(Boolean))].sort((a, b) => a.localeCompare(b));
                setLocations(flat);

                const la = Array.isArray(lawRes?.data?.lawareas)
                    ? lawRes.data.lawareas.map((x) => x.law_area).filter(Boolean)
                    : [];
                setLawAreas(la);

                // 2) selected user (NOT logged-in user)
                const userRes = await api.get(`detail/user/${userId}`);
                const u = userRes.data;

                setForm({
                    name: u?.name ?? '',
                    email: u?.email ?? '',
                    phone: u?.phone ?? '',
                    role: u?.role ?? '',
                    // if you store company as ObjectId, this should be u.company (id) or u.company._id if populated
                    company: u?.company?._id ?? u?.company ?? '',
                    location: Array.isArray(u?.location) ? u.location : (u?.location ? [u.location] : []),
                    lawarea: Array.isArray(u?.lawarea) ? u.lawarea : (u?.lawarea ? [u.lawarea] : []),
                    departments: Array.isArray(u?.departments) ? u.departments : (u?.departments ? [u.departments] : []),
                });
            } catch (e) {
                console.error(e);
                setSnackbar({
                    open: true,
                    severity: 'error',
                    msg: 'Failed to load selected user. Check /detail/user/:userId API (404/401?).',
                });
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [userId]);

    const handleUpdate = async () => {
        try {
            setSaving(true);
            await api.put(`detail/update-user/${userId}`, {
                phone: form.phone,
                role: form.role,
                company: form.company,
                location: form.location,
                lawarea: form.lawarea, // ✅ correct field
                departments: form.departments,
            });

            setSnackbar({ open: true, severity: 'success', msg: 'User updated.' });
            //setTimeout(() => navigate('/users'), 600);
        } catch (e) {
            console.error(e);
            setSnackbar({ open: true, severity: 'error', msg: 'Update failed.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout title="Edit User">
            <ThemeProvider theme={theme}>
                <Box sx={{ background: '#F3F6FB', minHeight: '100%', py: 2 }}>
                    <StyledContainer maxWidth="md">
                        <Typography variant="h6" color="primary" mb={2}>Edit User</Typography>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="First Name" value={firstName} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Last Name" value={lastName} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="E-mail" value={form.email} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth label="Mobile" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                </Grid>

                                <StyledDivider />

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel shrink>Role</InputLabel>
                                            <Select value={form.role || ''} onChange={(e) => setForm(p => ({ ...p, role: e.target.value }))} label="Role">
                                                <MenuItem value="seniormanager">Senior Manager</MenuItem>
                                                <MenuItem value="admin">admin</MenuItem>
                                                <MenuItem value="manager">manager</MenuItem>
                                                <MenuItem value="owner">owner</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel shrink>Company</InputLabel>
                                            <Select value={form.company || ''} onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))} label="Company">
                                                {companies.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel shrink>Location</InputLabel>
                                            <Select
                                                multiple
                                                value={form.location}
                                                onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))}
                                                renderValue={(selected) => selected.join(', ')}
                                                label="Location"
                                            >
                                                {locations.map((loc) => (
                                                    <MenuItem key={loc} value={loc}>
                                                        <Checkbox checked={form.location.includes(loc)} />
                                                        <ListItemText primary={loc} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel shrink>Law Area</InputLabel>
                                            <Select
                                                multiple
                                                value={form.lawarea}
                                                onChange={(e) => setForm(p => ({ ...p, lawarea: e.target.value }))}
                                                renderValue={(selected) => selected.join(', ')}
                                                label="Law Area"
                                            >
                                                {lawAreas.map((la) => (
                                                    <MenuItem key={la} value={la}>
                                                        <Checkbox checked={form.lawarea.includes(la)} />
                                                        <ListItemText primary={la} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel shrink>Departments</InputLabel>
                                            <Select
                                                multiple
                                                value={form.departments}
                                                onChange={(e) => setForm(p => ({ ...p, departments: e.target.value }))}
                                                renderValue={(selected) => selected.join(', ')}
                                                label="Departments"
                                            >
                                                {DEPARTMENT_OPTIONS.map((dep) => (
                                                    <MenuItem key={dep} value={dep}>
                                                        <Checkbox checked={form.departments.includes(dep)} />
                                                        <ListItemText primary={dep} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Button variant="outlined" color="secondary" fullWidth onClick={() => navigate('manage/users')} disabled={saving}>
                                                    Cancel
                                                </Button>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Button variant="contained" color="primary" fullWidth onClick={handleUpdate} disabled={saving}>
                                                    {saving ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Update User Info'}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                            <Alert severity={snackbar.severity} variant="filled">{snackbar.msg}</Alert>
                        </Snackbar>
                    </StyledContainer>
                </Box>
            </ThemeProvider>
        </Layout>
    );
}
